import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private dataStorageService: DataStorageService
  ) {}

  /**
   * Auth is an httpOnly cookie attached by the browser (see AuthInterceptor),
   * so there's no token to add here.
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders();
  }

  /**
   * Sends a GET request to the specified endpoint and returns an Observable of type T.
   *
   * @param endpoint - The endpoint to send the GET request to.
   * @returns An Observable of type T representing the response from the GET request.
   */
  public get<T>(
    endpoint: string,
    params?: { [key: string]: any }
  ): Observable<T> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          if (Array.isArray(params[key])) {
            params[key].forEach((value: any) => {
              httpParams = httpParams.append(`${key}[]`, value.toString());
            });
          } else {
            httpParams = httpParams.append(key, params[key].toString());
          }
        }
      });
    }

    return this.http.get<T>(`${this.apiUrl}${endpoint}`, {
      headers: this.getHeaders(),
      params: httpParams,
    });
  }

  /**
   * Sends a POST request to the specified endpoint with the provided data.
   *
   * @template T - The type of the response data.
   * @param {string} endpoint - The endpoint to send the request to.
   * @param {any} data - The data to send in the request body.
   * @returns {Observable<T>} - An observable that emits the response data.
   */
  public post<T>(endpoint: string, data: any, context?: HttpContext): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}${endpoint}`, data, {
      headers: this.getHeaders(),
      context,
    });
  }

  /**
   * Sends a PUT request to the specified endpoint with the provided data.
   *
   * @template T - The type of the response data.
   * @param {string} endpoint - The endpoint to send the request to.
   * @param {any} data - The data to send in the request body.
   * @returns {Observable<T>} - An observable that emits the response data.
   */
  public put<T>(endpoint: string, data: any): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}${endpoint}`, data, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Sends a DELETE request to the specified endpoint.
   *
   * @template T - The type of the response data.
   * @param {string} endpoint - The endpoint to send the request to.
   * @returns {Observable<T>} - An observable that emits the response data.
   */
  public delete<T>(endpoint: string, body?: any): Observable<T> {
    return this.http.request<T>('delete', `${this.apiUrl}${endpoint}`, {
      headers: this.getHeaders(),
      body: body,
    });
  }

  /**
   * Sends a POST request without authentication headers (for login endpoints)
   *
   * @template T - The type of the response data.
   * @param {string} endpoint - The endpoint to send the request to.
   * @param {any} data - The data to send in the request body.
   * @returns {Observable<T>} - An observable that emits the response data.
   */
  public postWithoutAuth<T>(endpoint: string, data: any): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}${endpoint}`, data);
  }

  /**
   * Sends an authenticated GET request expecting a binary body (e.g. a PDF
   * download), rather than the JSON envelope every other endpoint returns.
   *
   * @param endpoint - The endpoint to send the GET request to.
   * @returns An Observable of the raw Blob response.
   */
  public getBlob(endpoint: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${endpoint}`, {
      headers: this.getHeaders(),
      responseType: 'blob',
    });
  }

  /**
   * Sends a POST request with form data to the specified endpoint.
   *
   * @template T - The type of the response data.
   * @param {string} endpoint - The endpoint to send the request to.
   * @param {FormData} data - The form data to send in the request body.
   * @returns {Observable<T>} - An observable that emits the response data.
   */
  public postFormData<T>(endpoint: string, data: FormData): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}${endpoint}`, data, {
      headers: this.getHeaders(),
    });
  }
}
